class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = document.getElementById('cart-notification');
    this.header = document.querySelector('sticky-header');
    this.onBodyClick = this.handleBodyClick.bind(this);

    this.notification.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelectorAll('button[type="button"]').forEach((closeButton) =>
      closeButton.addEventListener('click', this.close.bind(this))
    );
  }

  connectedCallback() {
    if (this.autoCloseController) return;
    this.autoCloseController = new AbortController();
    this.autoClosePointers = new Set();
    this.autoCloseTouching = false;
    this.autoCloseHovered = false;
    this.autoCloseKeyboard = false;
    const signal = this.autoCloseController.signal;
    const listen = (target, type, fn, options = {}) => target.addEventListener(type, fn, { ...options, signal });
    listen(this.notification, 'pointerenter', (event) => {
      if (event.pointerType !== 'mouse') return;
      this.autoCloseHovered = true;
      this.stopAutoClose();
    });
    listen(this.notification, 'pointerleave', (event) => {
      if (event.pointerType !== 'mouse') return;
      this.autoCloseHovered = false;
      this.restartAutoClose();
    });
    listen(this.notification, 'pointerdown', (event) => {
      this.autoClosePointers.add(event.pointerId);
      this.autoCloseKeyboard = false;
      if (event.pointerType !== 'mouse') this.autoCloseHovered = false;
      this.stopAutoClose();
    }, { passive: true });
    const release = (event) => {
      if (!this.autoClosePointers.delete(event.pointerId)) return;
      this.restartAutoClose();
    };
    listen(window, 'pointerup', release, { passive: true, capture: true });
    listen(window, 'pointercancel', release, { passive: true, capture: true });
    listen(this.notification, 'touchstart', () => {
      this.autoCloseTouching = true;
      this.autoCloseKeyboard = false;
      this.autoCloseHovered = false;
      this.stopAutoClose();
    }, { passive: true });
    const finishTouch = (event) => {
      if (!this.autoCloseTouching) return;
      this.autoCloseTouching = event.touches.length > 0;
      this.restartAutoClose();
    };
    listen(window, 'touchend', finishTouch, { passive: true, capture: true });
    listen(window, 'touchcancel', finishTouch, { passive: true, capture: true });
    listen(this.notification, 'scroll', () => this.restartAutoClose(), { passive: true, capture: true });
    listen(this.notification, 'click', () => this.restartAutoClose());
    listen(this.notification, 'keydown', () => {
      this.autoCloseKeyboard = true;
      this.stopAutoClose();
    });
    listen(this.notification, 'focusout', (event) => {
      if (event.relatedTarget && this.notification.contains(event.relatedTarget)) return;
      this.autoCloseKeyboard = false;
      this.restartAutoClose();
    });
    listen(document, 'visibilitychange', () => {
      if (document.hidden) this.stopAutoClose();
      else this.restartAutoClose();
    });
  }

  disconnectedCallback() {
    this.close();
    this.autoCloseController?.abort();
    this.autoCloseController = null;
  }

  stopAutoClose() {
    clearTimeout(this.autoCloseTimer);
    this.autoCloseTimer = null;
  }

  canAutoClose() {
    return this.notification.classList.contains('active') && !document.hidden &&
      !this.autoCloseHovered && !this.autoCloseTouching && !this.autoCloseKeyboard && !this.autoClosePointers?.size &&
      !this.querySelector('[aria-busy="true"]');
  }

  restartAutoClose() {
    this.stopAutoClose();
    if (!this.canAutoClose()) return;
    this.autoCloseTimer = setTimeout(() => {
      this.autoCloseTimer = null;
      if (this.canAutoClose()) this.close();
    }, 3000);
  }

  open() {
    this.stopAutoClose();
    this.autoClosePointers?.clear();
    this.autoCloseKeyboard = Boolean(this.activeElement?.matches?.(':focus-visible'));
    this.autoCloseHovered = Boolean(window.matchMedia?.('(any-hover: hover)').matches && this.notification.matches(':hover'));
    this.notification.classList.add('animate', 'active');
    this.restartAutoClose();

    this.notification.addEventListener(
      'transitionend',
      () => {
        this.notification.focus();
        trapFocus(this.notification);
      },
      { once: true }
    );

    document.body.addEventListener('click', this.onBodyClick);

    this.dispatchCartViewEvent();
  }

  // The notification's outer element is server-rendered once at page load, so
  // its `cart` Liquid object reflects the pre-add state. The morphed children
  // (inserted from the /cart/add.js sections response in renderContents) are
  // post-add, but they don't expose the full cart shape we need for the event
  // payload. So we keep an explicit /cart.json fetch on open. Migrating to the
  // factory + filter would require re-rendering the notification element
  // itself in sections, which is out of scope for this PR.
  async dispatchCartViewEvent() {
    const { CartViewEvent } = window.StandardEvents || {};
    if (!CartViewEvent) return;

    try {
      const response = await fetch(`${routes.cart_url}.json`);
      const cart = await response.json();
      if (!cart?.currency) return;

      this.dispatchEvent(
        new CartViewEvent({
          context: 'dialog',
          cart: CartViewEvent.createCartFromAjaxResponse(cart),
        })
      );
    } catch (e) {
      // cart:view is informational; swallow fetch errors
    }
  }

  close() {
    this.stopAutoClose();
    this.autoClosePointers?.clear();
    this.autoCloseTouching = false;
    this.autoCloseKeyboard = false;
    this.autoCloseHovered = false;
    this.notification.classList.remove('active');
    document.body.removeEventListener('click', this.onBodyClick);

    removeTrapFocus(this.activeElement);
  }

  renderContents(parsedState) {
    this.cartItemKey = parsedState.key;
    this.getSectionsToRender().forEach((section) => {
      document.getElementById(section.id).innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector
      );
    });

    this.setupQuantityControl();
    if (this.header) this.header.reveal();
    this.open();
  }

  async setupQuantityControl() {
    const token = (this.quantityToken || 0) + 1;
    this.quantityToken = token;
    const key = this.cartItemKey;
    const product = document.getElementById('cart-notification-product');
    this.querySelector('.hilali-notification-quantity')?.remove();
    if (!product || !key) return;
    const panel = document.createElement('div');
    panel.className = 'hilali-notification-quantity';
    panel.innerHTML = '<span class="hilali-quantity-label">Adet</span><div class="hilali-quantity-controls"><button type="button" data-step="-1" aria-label="Adedi azalt" disabled>−</button><output aria-live="polite">…</output><button type="button" data-step="1" aria-label="Adedi artır" disabled>+</button></div><p class="hilali-quantity-error" role="alert" hidden></p>';
    product.insertAdjacentElement('afterend', panel);
    const buttons = [...panel.querySelectorAll('button')];
    const output = panel.querySelector('output');
    const error = panel.querySelector('.hilali-quantity-error');
    let item, busy = false;
    const showError = (message) => { error.textContent = message; error.hidden = false; };
    const refresh = () => {
      output.textContent = item.quantity;
      const rule = item.quantity_rule || {};
      buttons[0].disabled = busy || item.quantity - (rule.increment || 1) < Math.max(1, rule.min || 1);
      buttons[1].disabled = busy || (rule.max != null && item.quantity + (rule.increment || 1) > rule.max);
      panel.setAttribute('aria-busy', String(busy));
    };
    try {
      const response = await fetch(`${routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Sepet bilgisi alınamadı.');
      const cart = await response.json();
      if (this.quantityToken !== token) return;
      item = cart.items.find((line) => line.key === key);
      if (!item) { panel.remove(); return; }
      if (item.parent_relationship || item.instructions?.can_update_quantity === false) { panel.remove(); return; }
      refresh();
    } catch (e) { if (this.quantityToken === token) showError('Adet bilgisi alınamadı. Sepeti görüntüleyerek düzenleyebilirsiniz.'); return; }
    const removeButton = document.createElement('button');
removeButton.type = 'button';
removeButton.className = 'hilali-notification-remove';
removeButton.textContent = 'Ürünü kaldır';
panel.appendChild(removeButton);

removeButton.addEventListener('click', async () => {
  if (busy || this.quantityToken !== token) return;

  busy = true;
  error.hidden = true;
  removeButton.disabled = true;
  removeButton.textContent = 'Kaldırılıyor…';
  refresh();

  try {
    const response = await fetch(routes.cart_change_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({
        id: item.key,
        quantity: 0,
        sections: ['cart-icon-bubble', 'cart-notification-button'],
        sections_url: window.location.pathname
      })
    });

    const cart = await response.json();

    if (!response.ok || cart.errors) {
      throw new Error('Ürün kaldırılamadı. Lütfen tekrar deneyin.');
    }

    ['cart-icon-bubble', 'cart-notification-button'].forEach(id => {
      const target = document.getElementById(id);
      const html = cart.sections?.[id];
      if (!target || !html) return;

      const section = new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector('.shopify-section');

      if (section) target.innerHTML = section.innerHTML;
    });

    document.dispatchEvent(new CustomEvent('cart:updated'));

    if (
      typeof publish === 'function' &&
      typeof PUB_SUB_EVENTS !== 'undefined'
    ) {
      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'cart-notification-remove',
        productVariantId: item.variant_id,
        cartData: cart
      });
    }

    if (this.quantityToken === token) {
      this.quantityToken++;
      panel.remove();
      this.close();
    }
  } catch (e) {
    if (this.quantityToken === token) {
      showError(e.message || 'Ürün kaldırılamadı.');
    }
  } finally {
    busy = false;
    if (panel.isConnected && this.quantityToken === token) {
      removeButton.disabled = false;
      removeButton.textContent = 'Ürünü kaldır';
      refresh();
    }
  }
});
    buttons.forEach((button) => button.addEventListener('click', async () => {
      if (busy || this.quantityToken !== token) return;
      busy = true; error.hidden = true; refresh();
      const quantity = item.quantity + Number(button.dataset.step) * (item.quantity_rule?.increment || 1);
      try {
        const response = await fetch(routes.cart_change_url, {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: item.key, quantity, sections: this.getSectionsToRender().map((s) => s.id), sections_url: window.location.pathname })
        });
        const cart = await response.json();
        if (!response.ok || cart.errors) throw new Error(typeof cart.description === 'string' ? cart.description : typeof cart.errors === 'string' ? cart.errors : 'Adet güncellenemedi.');
        if (this.quantityToken !== token) return;
        const updated = cart.items.find((line) => line.key === item.key) || cart.items.find((line) => line.variant_id === item.variant_id && JSON.stringify(line.properties || {}) === JSON.stringify(item.properties || {}) && (line.selling_plan_allocation?.selling_plan?.id || null) === (item.selling_plan_allocation?.selling_plan?.id || null));
        if (!updated) { panel.remove(); return; }
        item = updated; this.cartItemKey = item.key;
        this.getSectionsToRender().forEach((section) => {
          const html = cart.sections?.[section.id];
          const target = document.getElementById(section.id);
          if (!html || !target) return;
          const node = new DOMParser().parseFromString(html, 'text/html').querySelector(section.selector || '.shopify-section');
          if (node) target.innerHTML = node.innerHTML;
        });
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-notification-quantity', productVariantId: item.variant_id, cartData: cart });
        }
      } catch (e) { if (this.quantityToken === token) showError(e.message || 'Adet güncellenemedi.'); }
      finally { busy = false; if (this.quantityToken === token && panel.isConnected) { refresh(); this.restartAutoClose(); } }
    }));
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-notification-product',
        selector: `[id="cart-notification-product-${this.cartItemKey}"]`,
      },
      {
        id: 'cart-notification-button',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  handleBodyClick(evt) {
    const target = evt.target;
    if (target !== this.notification && !target.closest('cart-notification')) {
      const disclosure = target.closest('details-disclosure, header-menu');
      this.activeElement = disclosure ? disclosure.querySelector('summary') : null;
      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-notification', CartNotification);
